import { Fragment } from 'react'

import { Card, Grid, Group, LabeledValue, Layout, Stack, Tag, Text } from '@loadsmart/miranda-react'

import { ChatContextProvider } from 'core/chat/ui/ChatContext'
import { ChatError } from 'core/chat/ui/ChatError'
import { ContainerDetails } from 'core/container-details/ui/ContainerDetails'
import { DrayageLoadDirection } from 'core/container-details/ui/DrayageLoadDirection'
import { getTruckLabel } from 'core/presenters/truckPresenter'
import { ShipmentTags } from 'core/shipment-tag/ui/ShipmentTags'
import { ShippingItems } from 'core/shipping-items/ui/ShippingItems'
import { ErrorBoundary } from 'core/ui/components/error-boundary/ErrorBoundary'
import { Item } from 'core/ui/components/item/Item'

import { equipmentSubtypes } from 'tender/presenter/tenderPresenter'

import { isDrayage, type Shipment } from '../domain/Shipment'
import {
  formatCargoValue,
  formatEquipmentCellText,
  formatMileage,
  formatSealNumber,
  formatSONumbers,
  formatTarp,
  formatTrailerNumber,
  formatValue,
} from '../presenters/shipmentPresenter'
import { getFormattedStopDate, getFormattedStops } from '../presenters/stopPresenter'
import Divider from './Divider'
import { DocumentCard } from './DocumentSection/DocumentCard'
import { MessagesBox } from './MessagesBox'
import { PaymentOverviewCard } from './PaymentOverviewCard'
import { ProNumber } from './ProNumber'
import { CapitalizedTag, ShipperLogo } from './ShipmentInfo.styles'
import { ShipmentNotes } from './ShipmentNotes'

export type ShipmentInfoProps = Readonly<{
  shipment: Shipment
  collapsed?: boolean
  refetchShipment: (uuid: string) => Promise<void>
}>

export function ShipmentInfo({ shipment, collapsed, refetchShipment }: ShipmentInfoProps) {
  const pickup = shipment.stops[0]
  const delivery = shipment.stops[shipment.stops.length - 1]

  const pickupDate = getFormattedStopDate(pickup)
  const deliveryDate = getFormattedStopDate(delivery)

  const mileage = formatMileage(shipment.mileage)

  const showProNumber = shipment.mode === 'LTL' || shipment.mode === 'VLTL'

  return (
    <Stack>
      <Card collapsible collapsed={collapsed} data-testid="shipment-card">
        <Card.Title>
          <Stack gap="spacing-5">
            <Layout.Box padding="none">
              <Layout.Group gap="spacing-2" align="center" style={{ flexWrap: 'wrap' }}>
                <CapitalizedTag variant="neutral" size="default">
                  {shipment.status}
                </CapitalizedTag>
                <ShipmentTags tags={shipment.tags} />
              </Layout.Group>
            </Layout.Box>
            <Stack gap="spacing-1">
              <Text variant="heading-md-bold" color="color-text-secondary">
                {getFormattedStops(shipment.stops)}
              </Text>
              <Text variant="body-md">{pickupDate}</Text>
            </Stack>
            <Group>
              <Stack gap="spacing-1" style={{ flexGrow: 1 }}>
                <Text variant="body-sm" color="color-text-tertiary">
                  Transit Estimate
                </Text>
                <Text variant="body-md">{formatValue(shipment.transitEstimate)}</Text>
              </Stack>
              <Stack gap="spacing-1">
                <Text variant="body-sm" color="color-text-tertiary">
                  Distance
                </Text>
                <Text variant="body-md">{mileage}</Text>
              </Stack>
            </Group>
          </Stack>
        </Card.Title>
        <Card.Body>
          <Stack gap="spacing-5">
            <Divider />
            <Stack gap="spacing-5">
              <Text variant="heading-sm-bold" as="h3">
                Shipper
              </Text>
              <Stack gap="spacing-2">
                {shipment.shipper?.logoEmailUrl && (
                  <ShipperLogo src={shipment.shipper.logoEmailUrl} height={40} alt="" />
                )}
                <Grid>
                  <LabeledValue
                    label="Shipper"
                    labelVariant="body-sm"
                    valueVariant="body-md"
                    labelColor="color-text-tertiary"
                    value={formatValue(shipment.shipper?.companyName)}
                  />
                  <LabeledValue
                    label="Shipper ref number"
                    labelVariant="body-sm"
                    valueVariant="number-md"
                    value={formatValue(shipment?.shipperRefNumber)}
                  />
                </Grid>
              </Stack>
            </Stack>
            <Divider />
            <Text variant="heading-sm-bold">Shipment Info</Text>
            <Grid>
              <Item
                label="Mode"
                value={
                  <Group align="flex-end" gap="spacing-1">
                    {shipment.mode}
                    {shipment.powerOnly && (
                      <Tag size="small" variant="warning">
                        Power Only
                      </Tag>
                    )}
                  </Group>
                }
              />
              {isDrayage(shipment) && (
                <DrayageLoadDirection domain="shipment" direction={shipment.container?.direction} />
              )}
              <Item
                label="Equipment"
                value={
                  <Layout.Group>
                    {formatEquipmentCellText({
                      equipment: getTruckLabel(shipment.equipmentType),
                      temperature: shipment.equipmentRequirements?.temperature,
                      temperatureUnits: shipment.equipmentRequirements?.temperatureUnit,
                      length: shipment.equipmentLength,
                    })}
                  </Layout.Group>
                }
              />
              {shipment.equipmentSubType && (
                <Item label="Sub equipment" value={equipmentSubtypes[shipment.equipmentSubType]} />
              )}
              {!isDrayage(shipment) && <Item label="Tarp" value={formatTarp(shipment)} />}
            </Grid>
            <Grid>
              <Stack gap="spacing-1">
                <Text variant="body-sm" color="color-text-tertiary">
                  Stop Date
                </Text>
                <Text variant="body-md">{pickupDate}</Text>
              </Stack>
              <Stack gap="spacing-1">
                <Text variant="body-sm" color="color-text-tertiary">
                  Stop Date
                </Text>
                <Text variant="body-md">{deliveryDate}</Text>
              </Stack>
            </Grid>
            <Grid>
              <Stack gap="spacing-1">
                <Text variant="body-sm" color="color-text-tertiary">
                  BOL number
                </Text>
                <Text variant="number-md">{formatValue(shipment?.bolNumber)}</Text>
              </Stack>
              <Stack gap="spacing-1">
                <Text variant="body-sm" color="color-text-tertiary">
                  SO numbers
                </Text>
                <Text variant="number-md">{formatSONumbers(shipment)}</Text>
              </Stack>
            </Grid>
            {showProNumber && (
              <Grid>
                <Stack gap="spacing-1">
                  <ProNumber
                    currentProNumber={shipment.proNumber}
                    shipmentUUID={shipment.uuid}
                    onUpdate={() => refetchShipment(shipment.uuid)}
                  />
                </Stack>
              </Grid>
            )}
            <Grid>
              <Stack gap="spacing-1">
                <Text variant="body-sm" color="color-text-tertiary">
                  PO numbers
                </Text>
                <Group>
                  {shipment?.poNumbers
                    ? shipment.poNumbers.map(number => (
                        <Fragment key={number}>
                          <Tag size="small">{number}</Tag>
                        </Fragment>
                      ))
                    : '-'}
                </Group>
              </Stack>
              <Stack gap="spacing-1">
                <Text variant="body-sm" color="color-text-tertiary">
                  Trailer number
                </Text>
                <Text variant="number-md">{formatTrailerNumber(shipment)}</Text>
              </Stack>
            </Grid>
            <Grid>
              {shipment.mode !== 'OCEAN' && shipment.mode !== 'DRAY' && (
                <Stack gap="spacing-1">
                  <Text variant="body-sm" color="color-text-tertiary">
                    Seal number
                  </Text>
                  <Text variant="number-md">{formatSealNumber(shipment)}</Text>
                </Stack>
              )}
              <Stack gap="spacing-1">
                <Text variant="body-sm" color="color-text-tertiary">
                  Cargo value
                </Text>
                <Text variant="number-md">{formatCargoValue(shipment)}</Text>
              </Stack>
            </Grid>
            <ShipmentNotes notes={shipment.spotQuote?.notes} />
          </Stack>
        </Card.Body>
      </Card>
      <ErrorBoundary fallback={ChatError}>
        <ChatContextProvider shipment={shipment}>
          <MessagesBox />
        </ChatContextProvider>
      </ErrorBoundary>
      {shipment.container && <ContainerDetails container={shipment.container} />}
      <ShippingItems measures={shipment.measures} shippingItems={shipment.items} />
      <DocumentCard shipment={shipment} />
      <PaymentOverviewCard shipment={shipment} />
    </Stack>
  )
}
